/**
 * @fileoverview Input/output schemas for story-weaknesses-v1.
 *
 * This agent reads the full written story and produces a ranked list of
 * concrete weaknesses ("choke points") tied to specific chapters. Unlike
 * story-feedback-v1 — which delivers opinionated multi-persona prose — this
 * agent is diagnostic and actionable: every issue names where it happens and
 * what kind of fix it implies.
 *
 * Input is deliberately minimal: just the concatenated story prose. No plan,
 * storyline, or snapshot data is provided — the agent reacts to the actual
 * manuscript.
 *
 * Output is flat (3 levels max) for Gemini API compatibility.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.fullStoryContent.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * A single diagnosed weakness. Each issue is a decision the author can act on:
 * it names the problem, rates how badly it hurts the book, scopes it to a
 * chapter range, and signals what kind of work the fix implies.
 */
const WeaknessIssueSchema = z.object({
  title: z.string().describe(
    'A short, specific title for the weakness (max ~8 words). '
    + 'Name the actual problem, not a generic category. '
    + 'Good: "Mara\'s betrayal arrives with no setup". Bad: "Pacing issue".'
  ),
  severity: z.enum(['critical', 'major', 'minor']).describe(
    'How badly this hurts the book. '
    + '"critical" = breaks the reader\'s experience or the story\'s logic; must fix. '
    + '"major" = noticeably weakens the book; should fix. '
    + '"minor" = a polish-level concern worth noting.'
  ),
  fixType: z.enum(['structural', 'character', 'content']).describe(
    'The kind of work the fix implies, so the author can triage by appetite. '
    + '"structural" = needs chapter/scene/beat-level changes (add, cut, reorder, resize). '
    + '"character" = needs changes to a character\'s arc, motivation, or consistency. '
    + '"content" = needs prose-level rewriting within existing chapters (a scene, a passage).'
  ),
  chapterStart: z.number().describe(
    'The chapter number where this weakness begins or is most visible. '
    + 'For a story-wide issue, use the first chapter where it surfaces.'
  ),
  chapterEnd: z.number().describe(
    'The chapter number where this weakness ends. Equal to chapterStart for a '
    + 'single-chapter issue; spans a range for issues that build across chapters.'
  ),
  summary: z.string().describe(
    'A single sentence stating what the weakness is. Plain, concrete, no preamble. '
    + 'This is the one-line "what this is" shown on the issue card.'
  ),
  detail: z.string().describe(
    '1-2 short paragraphs of Markdown explaining the weakness and why it matters. '
    + 'Cite the actual text — characters by name, specific scenes, concrete moments. '
    + 'Explain the consequence for the reader. Do not prescribe the fix here.'
  ),
});

export type WeaknessIssue = z.infer<typeof WeaknessIssueSchema>;

export const OutputSchema = z.object({
  overview: z.string().describe(
    'A 2-3 sentence neutral assessment of the manuscript\'s overall health — '
    + 'where it is strong and where the main choke points cluster. Editorial '
    + 'voice, no persona.'
  ),
  issues: z.array(WeaknessIssueSchema).describe(
    'The diagnosed weaknesses, ranked most-impactful first (critical before '
    + 'major before minor; within a severity, biggest reader impact first). '
    + 'Return an empty array only if the manuscript genuinely has no weaknesses '
    + 'worth surfacing.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
